import { FC, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import ContentTable, {
	type ContentTableColumn,
	type ContentTableItem
} from "src/components/ContentTable/ContentTable";
import Pagination from "src/components/Pagination/Pagination";
import { Button } from "src/components/ui/button";
import UserChipItem from "src/components/UserSearchItem/UserChipItem";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import useGroupMembers from "../../hooks/useGroupMembers";
import useRemoveGroupMember from "../../hooks/useRemoveGroupMember";
import { mapGroupMembersToContentTableItems } from "../../utils/mapGroupMembersToContentTableItems";
import AddGroupMembersModal from "../AddGroupMembersModal/AddGroupMembersModal";

interface Props {
	groupId: number;
}

const memberColumns: ContentTableColumn[] = [
	{
		key: "email",
		label: "Email",
		render: (item) => String(item.data?.email ?? "—")
	},
	{
		key: "role",
		label: "Role",
		render: (item) => String(item.data?.role ?? "—")
	},
	{
		key: "store",
		label: "Store",
		render: (item) => String(item.data?.storeName ?? "—")
	},
	{
		key: "source",
		label: "Source",
		render: (item) => {
			const source = item.data?.source as "Manual" | "Synced" | undefined;
			if (source === "Synced") {
				return (
					<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700 border border-zinc-200">
						Synced
					</span>
				);
			}
			return (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
					Manual
				</span>
			);
		}
	}
];

const GroupMembersSection: FC<Props> = ({ groupId }) => {
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);
	const [memberToRemove, setMemberToRemove] = useState<ContentTableItem | null>(
		null
	);
	const [isAddOpen, setIsAddOpen] = useState(false);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const { data, isLoading, isError } = useGroupMembers({
		groupId,
		page,
		pageSize: DEFAULT_ADMIN_PAGE_SIZE,
		search: debouncedSearch
	});

	const members = data?.results ?? [];
	const totalCount = data?.count ?? 0;
	const items = useMemo(
		() => mapGroupMembersToContentTableItems(members),
		[members]
	);

	const { mutate: removeMember, isLoading: isRemoving } =
		useRemoveGroupMember();

	return (
		<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
			<div className="flex items-center justify-between mb-4">
				<h3 className="font-heading text-lg font-medium text-foreground">
					Members
				</h3>
				<span className="text-[13px] text-muted-foreground">
					{totalCount} {totalCount === 1 ? "member" : "members"}
				</span>
			</div>

			{isError ? (
				<p role="alert" className="text-sm text-red-500">
					Something went wrong loading members.
				</p>
			) : (
				<>
					<ContentTable
						items={items}
						columns={memberColumns}
						isLoading={isLoading}
						showCreatedBy={false}
						showLastUpdated={false}
						showOrder={false}
						showStatus={false}
						nameColumnLabel="Name"
						searchValue={searchInput}
						onSearchChange={setSearchInput}
						searchPlaceholder="Search members..."
						renderName={(item) => (
							<UserChipItem
								user={{
									uuid: String(item.data?.uuid ?? ""),
									id: 0,
									name: item.name,
									role: String(item.data?.role ?? ""),
									store: 0,
									employee_id: null
								}}
							/>
						)}
						onDelete={(item) => setMemberToRemove(item)}
						canDelete={(item) =>
							(item.data?.source as string | undefined) !== "Synced"
						}
						searchRowExtra={
							<Button
								type="button"
								onClick={() => setIsAddOpen(true)}
								className="whitespace-nowrap"
							>
								<Plus className="h-4 w-4 mr-2" aria-hidden="true" />
								Add Members
							</Button>
						}
					/>

					<Pagination
						page={page}
						pageSize={DEFAULT_ADMIN_PAGE_SIZE}
						totalCount={totalCount}
						onPageChange={setPage}
						itemLabel="members"
					/>
				</>
			)}

			<ConfirmationModal
				isOpen={memberToRemove !== null}
				title="Remove this member from the group?"
				description="They will lose any access granted by this group. You can add them back later."
				onCancel={() => {
					if (!isRemoving) setMemberToRemove(null);
				}}
				cancelBtnProps={{ disabled: isRemoving }}
				onSubmit={() => {
					if (!memberToRemove) return;
					removeMember(
						{ groupId, userId: String(memberToRemove.id) },
						{ onSuccess: () => setMemberToRemove(null) }
					);
				}}
				submitBtnProps={{ isLoading: isRemoving }}
			/>

			<AddGroupMembersModal
				isOpen={isAddOpen}
				groupId={groupId}
				onClose={() => setIsAddOpen(false)}
			/>
		</div>
	);
};

export default GroupMembersSection;
